import React, { useCallback, useEffect, useMemo } from 'react'
import {
  QnStatesProps,
  RelationshipTypes,
  VehicleTypes,
  DisplacementTypes,
  CargoTypes,
  CargoQuantityTypes,
} from 'states/QnState'
import { CONTRACT_INFO_FORM, VEHICLE_STATE } from 'constants/qnState'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import { PAGE_PATH } from 'constants/pagePath'
import { useEditContractInfo, StatesProps } from 'utils/commonHooks'
import { StatusProps, success } from 'utils/status'
import {
  createOptions,
  splitFullWidthPlateNumber,
  convertWideKanaToHalfKana,
  nameKanaValidation,
  phoneNumberValidation,
} from 'utils/commonUtils'
import TextBoxGroup from 'components/elements/form/TextBoxGroup'
import RequestTemplate from 'components/templates/RequestTemplate'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import AccordionGroup from 'components/elements/form/AccordionGroup'
import Text from 'components/elements/texts/Text'
import InformationModal from 'components/elements/modal/InformationModal'
import styled from 'styled-components'
import { PageMessage } from 'constants/pageMessage'
import { isSjDomain, isKsDomain } from 'utils/domain'
import { useProgressPoint } from '../../utils/commonHooks'
import MultiTextBoxGroup from 'components/elements/form/MultiTextBoxGroup'
import NumbarPlateBike from 'images/number-plate_bike_full.png'
import NumberPlateVehicle from 'images/numbe-plate_vehicle_half.png'

/** 契約者との関係の選択肢 */
const relationshipOptions = createOptions(
  CONTRACT_INFO_FORM.relationship.options
)
/** バイクの排気量の選択肢 */
const bikeDisplacementOptions = createOptions(
  CONTRACT_INFO_FORM.bikeDisplacement.options
)
/** 最大積載量の選択肢 */
const cargoTypeOptions = createOptions(CONTRACT_INFO_FORM.cargo.options)

/** 搬送時の積み荷の量の選択肢 */
const cargoQuantityTypeOptions = createOptions(
  CONTRACT_INFO_FORM.cargoQuantity.options
)

/** 氏名最大入力文字数（スペースを除く） */
const maxNameKanaTotalLength = 29

/** ナンバープレートのバリデーション */
const numberPlateValidation = (target: string): string => {
  if (!target) return ''
  const regex = /^[ァ-ヶｦ-ﾟ\u3040-\u309F0-9０-９ーa-zA-ZＡ-Ｚａ-ｚ]+$/
  if (!regex.test(target)) {
    return '全角・半角カナ・かな・英数字で入力してください。'
  }
  return ''
}

/** 指定業者電話番号の入力内容のバリデーション */
const designatePartnerPhoneNumberValidation = (target: string): string => {
  if (!target) return ''
  const regex = /^[0-9０-９]+$/
  if (!regex.test(target)) {
    return '全角・半角数字で入力してください。'
  }
  return ''
}

/** JAF会員番号の入力内容のバリデーション*/
const jafMembershipNumberValidation = (target: string): string => {
  if (!target) return ''
  const regex = /^[0-9０-９]+$/
  if (!regex.test(target)) {
    return '全角・半角数字で入力してください。'
  }
  if (target.length < 12) {
    return '会員番号は12桁で入力してください。'
  }
  return ''
}

/** JAF会員名の入力内容のエラーメッセージ */
const jafMembershipnameKanaValidation = (target: string): string => {
  if (!target) return ''
  const regex = /^[ァ-ヶー]{2,}$/
  if (!regex.test(target)) return '全角カナ文字で入力してください。'
  return ''
}

/** サロゲートペアのチェック（画面遷移時使用） */
const hasSurrogatePair = (item: string): boolean =>
  /[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(item)

/** ナンバープレート情報0詰め処理 */
function addAZeroBefore(target: string): string {
  return target && target.length < 4 ? target.padStart(4, '0') : target
}

/** QN-06 契約情報入力画面 */
const ContractInfo: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editState, setEditingItem, setContractInfo] = useEditContractInfo()
  const [infoFlag, setInfoFlag] = React.useState<boolean>(false)
  const [infoMessage, setInfoMessage] = React.useState<string>()
  const { state, setState } = props
  const {
    nameKana,
    lastName,
    firstName,
    firstNumber,
    secondNumber,
    lastNumber,
    relationship,
    contractorName,
    plateNumber,
    motorbikeAndOtherPlateNumber,
    region,
    classificationNumber,
    kana,
    designatedNumber,
    vehicleType,
    bikeDisplacement,
    cargo,
    cargoQuantity,
    designatePartnerConfirmation,
    designatePartnerPhoneNumber,
    designatePartnerName,
    jafMembershipConfirmation,
    jafMembershipNumber,
    jafMembershipName,
  } = editState.contractInfo
  /** ナンバープレート最大入力文字数 */
  const maxPlateNumberTotalLength = (): number => {
    if (convertWideKanaToHalfKana(designatedNumber).length < 4) {
      return 56
    }
    return 60 - convertWideKanaToHalfKana(designatedNumber).length
  }
  /** ドメインごとの車両種別の選択肢 */
  const vehicleTypeOptions = useMemo(
    () => createOptions<VehicleTypes>(CLIENT_SETTINGS.options.vehicleType),
    [CLIENT_SETTINGS]
  )

  /** ドメインごとのJAF会員希望メッセージ */
  const JafMembershipDescription = useMemo(() => {
    return isKsDomain()
      ? PageMessage['contractInformation.JafMembershipKsDescription']
      : isSjDomain()
      ? PageMessage['contractInformation.JafMembershipSjDescription']
      : PageMessage['contractInformation.JafMembershipSaficDescription']
  }, [])

  /** 名前(氏)の入力内容のエラーメッセージ */
  const lastNameError = nameKanaValidation(lastName)

  /** 名前(名)の入力内容のエラーメッセージ */
  const firstNameError = nameKanaValidation(firstName)

  const nameKanaError = nameKanaValidation(lastName + firstName)

  /** 電話番号(1つ目)の入力内容のエラーメッセージ */
  const firstPhoneNumberError = phoneNumberValidation(firstNumber, 3)

  /** 電話番号(2つ目)の入力内容のエラーメッセージ */
  const secondPhoneNumberError = phoneNumberValidation(secondNumber, 4)

  /** 電話番号(3つ目)の入力内容のエラーメッセージ */
  const lastPhoneNumberError = phoneNumberValidation(lastNumber, 4)

  const phoneNumberError = phoneNumberValidation(
    firstNumber + secondNumber + lastNumber,
    11
  )

  const plateNumberConcatError = numberPlateValidation(
    motorbikeAndOtherPlateNumber
  )
  const plateRegionError = numberPlateValidation(region)
  const plateClassificationError = numberPlateValidation(classificationNumber)
  const plateKanaError = numberPlateValidation(kana)
  const plateDesignatedNumberError = numberPlateValidation(designatedNumber)

  const plateNumberError =
    vehicleType === VehicleTypes.other || vehicleType === VehicleTypes.motorbike
      ? plateNumberConcatError
      : numberPlateValidation(
          region + classificationNumber + kana + designatedNumber
        )

  const designatePartnerPhoneNumberError = designatePartnerConfirmation
    ? designatePartnerPhoneNumberValidation(designatePartnerPhoneNumber)
    : ''

  const jafMembershipNumberError = jafMembershipConfirmation
    ? jafMembershipNumberValidation(jafMembershipNumber)
    : ''
  const jafMembershipNameError =
    jafMembershipConfirmation && jafMembershipName !== ''
      ? jafMembershipnameKanaValidation(jafMembershipName)
      : ''

  /** 入力内容のValidationエラー状態 */
  const isDisabled = React.useMemo(() => {
    /** ナンバープレート未入力判定 */
    const isNotInputPlateNumber =
      vehicleType === VehicleTypes.other ||
      vehicleType === VehicleTypes.motorbike
        ? !motorbikeAndOtherPlateNumber
        : !region || !classificationNumber || !kana || !designatedNumber

    /** 未入力のパラメータの有無 */
    const hasNotInputInformation =
      !lastName ||
      !firstName ||
      !firstNumber ||
      !secondNumber ||
      !lastNumber ||
      isNotInputPlateNumber ||
      !vehicleType ||
      (jafMembershipConfirmation && !jafMembershipNumber) ||
      (designatePartnerConfirmation && !designatePartnerPhoneNumber) ||
      (vehicleType?.includes(VehicleTypes.motorbike) && !bikeDisplacement) ||
      (vehicleType?.includes(VehicleTypes.truck) && !cargo)

    /** バリデーションに該当の有無 */
    const hasValidationError =
      nameKanaError !== '' ||
      phoneNumberError !== '' ||
      plateNumberError !== '' ||
      designatePartnerPhoneNumberError !== '' ||
      jafMembershipNumberError !== '' ||
      jafMembershipNameError !== ''

    return hasNotInputInformation || hasValidationError
  }, [
    lastName,
    firstName,
    firstNumber,
    secondNumber,
    lastNumber,
    region,
    classificationNumber,
    kana,
    designatedNumber,
    vehicleType,
    motorbikeAndOtherPlateNumber,
    nameKanaError,
    jafMembershipConfirmation,
    jafMembershipNumber,
    jafMembershipName,
    designatePartnerConfirmation,
    designatePartnerPhoneNumber,
    designatePartnerName,
    vehicleType,
    bikeDisplacement,
    cargo,
  ])

  const troubleScreenPath: Record<string, string> = {
    [VEHICLE_STATE.ACCIDENT]: PAGE_PATH.ACCIDENT,
    [VEHICLE_STATE.EMPTY_FUEL]: PAGE_PATH.EMPTY_FUEL,
    [VEHICLE_STATE.ENGINE_TROUBLE]: PAGE_PATH.ENGINE_TROUBLE,
    [VEHICLE_STATE.FALLING_WHEEL]: PAGE_PATH.FALLING_WHEEL,
    [VEHICLE_STATE.FLAT_TIRE]: PAGE_PATH.FLAT_TIRE,
    [VEHICLE_STATE.LOSS_KEY]: PAGE_PATH.LOSS_KEY,
    [VEHICLE_STATE.OTHERS]: PAGE_PATH.OTHERS,
  }

  /** 遷移先分岐処理 */
  const transitionDestination = useMemo(() => {
    return isSjDomain() || isKsDomain()
      ? troubleScreenPath[state.vehicleState || PAGE_PATH.VEHICLE_STATE]
      : PAGE_PATH.SERVICE_INFORMATION
  }, [])

  /** 戻り先 */
  const backTo = useMemo(() => {
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType])

  /** 遷移先 */
  const fowardPage = useMemo(() => {
    const contractorHasSurrogate =
      relationship !== RelationshipTypes.self &&
      hasSurrogatePair(contractorName)
    const partnerNameHasSurrogate =
      designatePartnerConfirmation && hasSurrogatePair(designatePartnerName)
    return contractorHasSurrogate || partnerNameHasSurrogate
      ? PAGE_PATH.ERROR
      : PAGE_PATH.CONFIRMATION
  }, [
    relationship,
    contractorName,
    designatePartnerConfirmation,
    designatePartnerName,
  ])

  /** 決定ボタン押下後の処理 */
  const forwardAction = useCallback((): StatusProps => {
    if (relationship === RelationshipTypes.self) {
      return setState({
        ...state,
        contractInfo: {
          ...editState.contractInfo,
          contractorName: nameKana,
        },
      })
    }
    return setState({
      ...state,
      contractInfo: editState.contractInfo,
    })
  }, [state, editState.contractInfo])

  /** 名前（カナ）変更時のコールバック */
  /** 姓名の結合 */
  useEffect(() => {
    if (!!firstName || !!lastName) {
      setContractInfo({ nameKana: `${lastName} ${firstName}` })
    } else {
      setContractInfo({ nameKana: '' })
    }
  }, [firstName, lastName])

  /** 姓変更時 */
  const handleLastNameChanged = useCallback(
    (value: string) => {
      setContractInfo({
        lastName: value,
      })
    },
    [setContractInfo]
  )

  /** 名変更時 */
  const handleFirstNameChanged = useCallback(
    (value: string) => {
      setContractInfo({
        firstName: value,
      })
    },
    [setContractInfo]
  )

  /** 電話番号変更時のコールバック */
  /** 電話番号の結合 */
  useEffect(() => {
    setContractInfo({
      phoneNumber: `${firstNumber}${secondNumber}${lastNumber}`,
    })
  }, [firstNumber, secondNumber, lastNumber])

  /** 市外局番3桁変更時 */
  const handleFirstNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        firstNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 上4桁変更時 */
  const handleSecondNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        secondNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 下4桁変更時 */
  const handleLastNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        lastNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 契約者との関係変更時のコールバック */
  const handleRelationshipChanged = useCallback(
    (value: RelationshipTypes) => {
      setContractInfo({
        relationship: value,
      })
      // 選択内容ごとにメッセージを表示
      switch (value) {
        case RelationshipTypes.friend:
        case RelationshipTypes.employee:
        case RelationshipTypes.other:
          setInfoFlag(true)
          setInfoMessage(
            `${CONTRACT_INFO_FORM.relationship.options[value]}をご選択された場合、確認のために後ほどオペレーターよりご連絡させていただくことがございますのでご了承ください。`
          )
          break
        default:
          break
      }
    },
    [setContractInfo]
  )

  /** 契約者名の変更コールバック */
  useEffect(() => {
    if (relationship === RelationshipTypes.self) {
      setContractInfo({ contractorName: nameKana })
    }
  }, [state.contractInfo.contractorName])

  const handleContractUserNameChanged = useCallback(
    (value: string) => {
      setContractInfo({
        contractorName: value,
      })
    },
    [setContractInfo]
  )

  /** ナンバープレート情報変更時のコールバック */

  /** 単一入力欄・分割入力欄入力時plateNumberへの値の反映 */
  useEffect(() => {
    if (
      vehicleType === VehicleTypes.other ||
      vehicleType === VehicleTypes.motorbike
    ) {
      motorbikeAndOtherPlateNumber
        ? setContractInfo({
            plateNumber: motorbikeAndOtherPlateNumber,
            region: splitFullWidthPlateNumber(motorbikeAndOtherPlateNumber)
              .region,
            classificationNumber: splitFullWidthPlateNumber(
              motorbikeAndOtherPlateNumber
            ).classNum,
            kana: splitFullWidthPlateNumber(motorbikeAndOtherPlateNumber).kana,
            designatedNumber: splitFullWidthPlateNumber(
              motorbikeAndOtherPlateNumber
            ).design,
          })
        : setContractInfo({
            plateNumber: '',
            region: '',
            classificationNumber: '',
            kana: '',
            designatedNumber: '',
          })
    } else {
      region || classificationNumber || kana || designatedNumber
        ? setContractInfo({
            plateNumber: `${region}${classificationNumber}${kana}${addAZeroBefore(
              designatedNumber
            )}`,
            motorbikeAndOtherPlateNumber: `${region}${classificationNumber}${kana}${addAZeroBefore(
              designatedNumber
            )}`,
          })
        : setContractInfo({ plateNumber: '', motorbikeAndOtherPlateNumber: '' })
    }
  }, [
    vehicleType,
    plateNumber,
    motorbikeAndOtherPlateNumber,
    region,
    classificationNumber,
    kana,
    designatedNumber,
  ])

  /** バイク・その他入力欄変更時 */
  const handleBikeAndOtherNumber = useCallback(
    (value: string) => {
      setContractInfo({
        motorbikeAndOtherPlateNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 地域名変更時*/
  const handleRegionChanged = useCallback(
    (value: string) => {
      setContractInfo({
        region: value,
      })
    },
    [setContractInfo]
  )

  /** 分類番号変更時*/
  const handleClassificationNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        classificationNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 平仮名等変更時*/
  const handleKanaChanged = useCallback(
    (value: string) => {
      setContractInfo({
        kana: value,
      })
    },
    [setContractInfo]
  )

  /** 一連指定番号変更時*/
  const handleDesignatedNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        designatedNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 車両種別変更時のコールバック */
  const handleVehicleTypeChanged = useCallback(
    (value: VehicleTypes) => {
      setContractInfo({
        vehicleType: value,
      })
      // 選択内容ごとにメッセージを表示
      if (isSjDomain() || isKsDomain()) {
        switch (value) {
          case VehicleTypes.truck:
          case VehicleTypes.bus:
            setInfoFlag(true)
            setInfoMessage(
              `${CONTRACT_INFO_FORM.vehicleType.options[value]}の場合、オペレーターまたはスタッフよりお車のサイズを確認するため、ご連絡を差し上げる場合があります。`
            )
            break
          case VehicleTypes.other:
            setInfoFlag(true)
            setInfoMessage(
              `後ほどオペレーターよりお車について確認のためご連絡を差し上げます。`
            )
            break
          default:
            break
        }
      }
    },
    [setContractInfo]
  )

  /** 排気量選択変更時のコールバック */
  const handleBikeDisplacementChanged = useCallback(
    (value: DisplacementTypes) => {
      setContractInfo({
        bikeDisplacement: value,
      })
    },
    [setContractInfo]
  )

  /** 最大積載量選択変更時のコールバック */
  const handleCargoChanged = useCallback(
    (value: CargoTypes) => {
      setContractInfo({
        cargo: value,
      })
    },
    [setContractInfo]
  )

  /** 搬送時の積み荷の量選択変更時のコールバック */
  const handleCargoQuantityChanged = useCallback(
    (value: CargoQuantityTypes) => {
      setContractInfo({
        cargoQuantity: value,
      })
    },
    [setContractInfo]
  )
  /** 指定業者アコーディオンの開閉状態変更時のコールバック */
  const handleDesignatePartnerConfirmationChanged = useCallback(
    (isOpened: boolean) => {
      setContractInfo({
        designatePartnerConfirmation: isOpened,
        jafMembershipConfirmation:
          !isOpened && jafMembershipConfirmation ? true : false,
      })
    },
    [setContractInfo]
  )

  /** 指定業者の電話番号変更時のコールバック */
  const handledesignatePartnerPhoneNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        designatePartnerPhoneNumber: value,
      })
    },
    [setContractInfo]
  )

  /** 指定業者名変更時のコールバック */
  const handledesignatePartnerNameChanged = useCallback(
    (value: string) => {
      setContractInfo({
        designatePartnerName: value,
      })
    },
    [setContractInfo]
  )

  /** JAF加入アコーディオンの開閉状態変更時のコールバック */
  const handleJafMembershipConfirmationChanged = useCallback(
    (isOpened: boolean) => {
      setContractInfo({
        jafMembershipConfirmation: isOpened,
        designatePartnerConfirmation:
          !isOpened && designatePartnerConfirmation ? true : false,
      })
    },
    [setContractInfo]
  )

  /** JAF会員番号入力内容変更時のコールバック */
  const handleJafMembershipNumberChanged = useCallback(
    (value: string) => {
      setContractInfo({
        jafMembershipNumber: value,
      })
    },
    [setContractInfo]
  )

  /** JAF会員名入力内容変更時のコールバック */
  const handleJafMembershipNameChanged = useCallback(
    (value: string) => {
      setContractInfo({
        jafMembershipName: value,
      })
    },
    [setContractInfo]
  )

  /** メッセージモーダルを閉じる */
  const closeModal = useCallback(() => {
    setInfoFlag(false)
  }, [])

  /** トラブル画面でバイク選択時 */
  useEffect(() => {
    if (
      editState.vehicleStateInfo.isMotorbike &&
      !props.state.confirmationChangeType
    ) {
      setContractInfo({
        vehicleType: VehicleTypes.motorbike,
      })
    }
  }, [])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setContractInfo({
      bikeDisplacement:
        bikeDisplacement === '' ? DisplacementTypes.under50 : bikeDisplacement,
      relationship: relationship === '' ? RelationshipTypes.self : relationship,
      vehicleType: vehicleType === '' ? VehicleTypes.autoMobile : vehicleType,
      cargo: cargo === '' ? CargoTypes.lightTruck : cargo,
      cargoQuantity:
        cargoQuantity === '' ? CargoQuantityTypes.full : cargoQuantity,
    })
  }, [])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = useCallback(() => {
    setEditingItem({
      ...editState,
      contractInfo: {
        ...state.contractInfo,
      },
    })
    return success()
  }, [state.confirmationChangeType, state.contractInfo])

  return (
    <RequestTemplate
      title={PageMessage['contractInformation.title']}
      showContact
      progress={useProgressPoint()}
      description={PageMessage['contractInformation.description']}
      forwardLabel={PageMessage['contractInformation.button']}
      forwardTo={fowardPage}
      forwardAction={forwardAction}
      isDisabled={isDisabled}
      backAction={backAction}
      backTo={backTo}
      showBack
      notClearEditingItem
      content={
        <>
          <MultiTextBoxGroup
            title={CONTRACT_INFO_FORM.nameKana.formName}
            isRequired
            fields={[
              {
                fieldKey: 'lastName',
                text: lastName,
                placeholder:
                  '入力例：' + CLIENT_SETTINGS.label.contractInfo.lastName,
                maxLength: {
                  length:
                    maxNameKanaTotalLength -
                    convertWideKanaToHalfKana(firstName).length,
                  checkHalfStr: true,
                },
                width: '50%',
                onChanged: handleLastNameChanged,
                isInvalid: lastNameError !== '',
              },
              {
                fieldKey: 'firstName',
                text: firstName,
                placeholder: CLIENT_SETTINGS.label.contractInfo.firstName,
                maxLength: {
                  length:
                    maxNameKanaTotalLength -
                    convertWideKanaToHalfKana(lastName).length,
                  checkHalfStr: true,
                },
                width: '46%',
                onChanged: handleFirstNameChanged,
                isInvalid: firstNameError !== '',
              },
            ]}
            validationMessage={nameKanaError}
          />
          <MultiTextBoxGroup
            title={CONTRACT_INFO_FORM.phoneNumber.formName}
            isRequired
            fields={[
              {
                fieldKey: 'firstNumber',
                text: firstNumber,
                placeholder:
                  '入力例：' + CONTRACT_INFO_FORM.phoneNumber.label[0],
                maxLength: { length: 3 },
                width: '36%',
                onChanged: handleFirstNumberChanged,
                isInvalid: firstPhoneNumberError !== '',
              },
              {
                fieldKey: 'secondNumber',
                text: secondNumber,
                placeholder: CONTRACT_INFO_FORM.phoneNumber.label[1],
                maxLength: { length: 4 },
                width: '28%',
                onChanged: handleSecondNumberChanged,
                isInvalid: secondPhoneNumberError !== '',
              },
              {
                fieldKey: 'lastNumber',
                text: lastNumber,
                placeholder: CONTRACT_INFO_FORM.phoneNumber.label[2],
                maxLength: { length: 4 },
                width: '28%',
                onChanged: handleLastNumberChanged,
                isInvalid: lastPhoneNumberError !== '',
              },
            ]}
            validationMessage={phoneNumberError}
          />
          <RadioButtonGroup
            title={CONTRACT_INFO_FORM.relationship.formName}
            isRequired
            options={relationshipOptions}
            selectedValue={relationship}
            onValueChanged={handleRelationshipChanged}
          />
          {relationship === RelationshipTypes.self ? null : (
            <TextBoxGroup
              title={CONTRACT_INFO_FORM.contractorName.formName}
              isOption
              description={CONTRACT_INFO_FORM.contractorName.label}
              caution={CONTRACT_INFO_FORM.contractorName.caution}
              placeholder={CLIENT_SETTINGS.label.contractInfo.contractorName}
              maxLength={{ length: 30, checkHalfStr: true }}
              text={contractorName}
              onChanged={handleContractUserNameChanged}
            />
          )}
          <RadioButtonGroup
            title={CONTRACT_INFO_FORM.vehicleType.formName}
            isRequired
            options={vehicleTypeOptions}
            selectedValue={vehicleType}
            onValueChanged={handleVehicleTypeChanged}
          />
          {(isSjDomain() || isKsDomain()) &&
            vehicleType?.includes(VehicleTypes.motorbike) && (
              <RadioButtonGroup
                title={`${CONTRACT_INFO_FORM.bikeDisplacement.formName}を選択`}
                isRequired
                options={bikeDisplacementOptions}
                selectedValue={bikeDisplacement}
                onValueChanged={handleBikeDisplacementChanged}
              />
            )}
          {(isSjDomain() || isKsDomain()) &&
            vehicleType?.includes(VehicleTypes.truck) && (
              <>
                <RadioButtonGroup
                  title={`${CONTRACT_INFO_FORM.cargo.formName}を選択`}
                  isRequired
                  options={cargoTypeOptions}
                  selectedValue={cargo}
                  onValueChanged={handleCargoChanged}
                />
                <RadioButtonGroup
                  title={`${CONTRACT_INFO_FORM.cargoQuantity.formName}を選択`}
                  isRequired
                  options={cargoQuantityTypeOptions}
                  selectedValue={cargoQuantity}
                  onValueChanged={handleCargoQuantityChanged}
                />
              </>
            )}
          {vehicleType === VehicleTypes.other ||
          vehicleType === VehicleTypes.motorbike ? (
            <TextBoxGroup
              title={CONTRACT_INFO_FORM.plateNumber.formName}
              fieldKey="concatPlateNumber"
              isRequired
              description={CONTRACT_INFO_FORM.plateNumber.description}
              placeholder={CONTRACT_INFO_FORM.plateNumber.label[0]}
              maxLength={{
                length: 60,
                checkHalfStr: true,
              }}
              text={motorbikeAndOtherPlateNumber}
              onChanged={handleBikeAndOtherNumber}
              image={
                vehicleType === VehicleTypes.motorbike
                  ? NumbarPlateBike
                  : undefined
              }
              isInvalid={plateNumberConcatError !== ''}
              validationMessage={plateNumberError}
              width="100%"
            />
          ) : (
            <MultiTextBoxGroup
              title={CONTRACT_INFO_FORM.plateNumber.formName}
              isRequired
              description={CONTRACT_INFO_FORM.plateNumber.description}
              image={NumberPlateVehicle}
              fields={[
                {
                  fieldKey: 'region',
                  text: region,
                  placeholder: CONTRACT_INFO_FORM.plateNumber.label[1],
                  maxLength: {
                    length:
                      maxPlateNumberTotalLength() -
                      convertWideKanaToHalfKana(classificationNumber + kana)
                        .length,
                    checkHalfStr: true,
                  },
                  onChanged: handleRegionChanged,
                  width: '45%',
                  isInvalid: plateRegionError !== '',
                },
                {
                  fieldKey: 'classificationNumber',
                  text: classificationNumber,
                  placeholder: CONTRACT_INFO_FORM.plateNumber.label[2],
                  maxLength: {
                    length:
                      maxPlateNumberTotalLength() -
                      convertWideKanaToHalfKana(region + kana).length,
                    checkHalfStr: true,
                  },
                  onChanged: handleClassificationNumberChanged,
                  width: '18%',
                  isInvalid: plateClassificationError !== '',
                },
                {
                  fieldKey: 'kana',
                  text: kana,
                  placeholder: CONTRACT_INFO_FORM.plateNumber.label[3],
                  maxLength: {
                    length:
                      maxPlateNumberTotalLength() -
                      convertWideKanaToHalfKana(region + classificationNumber)
                        .length,
                    checkHalfStr: true,
                  },
                  onChanged: handleKanaChanged,
                  width: '14%',
                  isInvalid: plateKanaError !== '',
                },
                {
                  fieldKey: 'designatedNumber',
                  text: designatedNumber,
                  placeholder: CONTRACT_INFO_FORM.plateNumber.label[4],
                  maxLength: {
                    length:
                      60 -
                      convertWideKanaToHalfKana(
                        region + classificationNumber + kana
                      ).length,
                    checkHalfStr: true,
                  },
                  onChanged: handleDesignatedNumberChanged,
                  width: '19%',
                  isInvalid: plateDesignatedNumberError !== '',
                },
              ]}
              validationMessage={plateNumberError}
            />
          )}
          {/* お客様指定業者 */}
          <AccordionGroup
            title={CONTRACT_INFO_FORM.designatePartnerConfirmation.formName}
            label={CONTRACT_INFO_FORM.designatePartnerConfirmation.label}
            isOpened={designatePartnerConfirmation}
            noMargin
            onChanged={handleDesignatePartnerConfirmationChanged}
          >
            <AccordionChildArea
              backgroundColor={CLIENT_SETTINGS.colors.background}
            >
              <DesignatePartnerTextArea>
                <ul
                  style={{
                    margin: '0',
                  }}
                >
                  {PageMessage[
                    'contractInformation.designatePartnerDescription.open'
                  ].map((message, i) => (
                    <li key={i}>
                      <Text>{message}</Text>
                    </li>
                  ))}
                </ul>
              </DesignatePartnerTextArea>
              <TextBoxGroup
                title={`${CONTRACT_INFO_FORM.designatePartnerPhoneNumber.formName}（ハイフン不要）`}
                fieldKey="designatePartnerPhoneNumber"
                isRequired
                placeholder={
                  CONTRACT_INFO_FORM.designatePartnerPhoneNumber.label
                }
                text={designatePartnerPhoneNumber}
                maxLength={{ length: 12 }}
                onChanged={handledesignatePartnerPhoneNumberChanged}
                isInvalid={designatePartnerPhoneNumberError !== ''}
                validationMessage={designatePartnerPhoneNumberError}
                width="100%"
              />
              <TextBoxGroup
                title={CONTRACT_INFO_FORM.designatePartnerName.formName}
                fieldKey="designatePartnerName"
                isOption
                placeholder={CONTRACT_INFO_FORM.designatePartnerName.label}
                text={designatePartnerName}
                maxLength={{ length: 30, checkHalfStr: true }}
                onChanged={handledesignatePartnerNameChanged}
                width="100%"
              />
            </AccordionChildArea>
          </AccordionGroup>
          {!designatePartnerConfirmation && (
            <DesignatePartnerArrangementDescription>
              {PageMessage[
                'contractInformation.designatePartnerDescription.close'
              ].map((message, i) => (
                <Text key={i}>{message}</Text>
              ))}
            </DesignatePartnerArrangementDescription>
          )}
          {/* お客様指定業者 */}
          {/* JAF関連 */}
          <AccordionGroup
            title={''}
            label={CONTRACT_INFO_FORM.jafMembershipConfirmation.label}
            isOpened={jafMembershipConfirmation}
            noMargin
            onChanged={handleJafMembershipConfirmationChanged}
          >
            <AccordionChildArea
              backgroundColor={CLIENT_SETTINGS.colors.background}
            >
              <JafTextArea>
                <ul
                  style={{
                    margin: '0',
                  }}
                >
                  {JafMembershipDescription.map((message, i) => (
                    <li key={i}>
                      <Text>{message}</Text>
                    </li>
                  ))}
                </ul>
              </JafTextArea>
              <TextBoxGroup
                title={`${CONTRACT_INFO_FORM.jafMembershipNumber.formName}を入力`}
                fieldKey="jafMembershipNumber"
                isRequired
                placeholder={CONTRACT_INFO_FORM.jafMembershipNumber.label}
                text={jafMembershipNumber}
                maxLength={{ length: 12 }}
                onChanged={handleJafMembershipNumberChanged}
                isInvalid={jafMembershipNumberError !== ''}
                validationMessage={jafMembershipNumberError}
                width="100%"
              />

              <TextBoxGroup
                title={`${CONTRACT_INFO_FORM.jafMembershipName.formName}を入力（同一の場合は不要）`}
                fieldKey="jafMembershipName"
                placeholder={CONTRACT_INFO_FORM.jafMembershipName.label}
                text={jafMembershipName}
                maxLength={{ length: 30, checkHalfStr: true }}
                onChanged={handleJafMembershipNameChanged}
                isInvalid={jafMembershipNameError !== ''}
                validationMessage={jafMembershipNameError}
                width="100%"
              />
            </AccordionChildArea>
          </AccordionGroup>
          {!jafMembershipConfirmation && (
            <JafArrangementDescription>
              {PageMessage[
                isKsDomain()
                  ? 'contractInformation.JAFKsDescription'
                  : 'contractInformation.JAFDescription'
              ].map((message, i) => (
                <Text key={i}>{message}</Text>
              ))}
            </JafArrangementDescription>
          )}
          <InformationModal
            on={infoFlag}
            content={infoMessage}
            greyBack
            textCenter
            closeLabel="OK"
            closeFunction={closeModal}
          />
        </>
      }
    />
  )
}

/** アコーディオンの子要素を配置するスタイル設定div */
const AccordionChildArea = styled.div<{ backgroundColor: string }>`
  background-color: ${(props): string => props.backgroundColor};
`
/** お客様指定業者の説明テキストを配置するスタイル設定div */
const DesignatePartnerTextArea = styled.div`
  padding-right: 20px;
`
/** お客様指定業者押下時のテキストを配置するスタイル設定div */
const DesignatePartnerArrangementDescription = styled.div`
  margin-top: 20px;
`
/** Jaf加入情報の説明テキストを配置するスタイル設定div */
const JafTextArea = styled.div`
  padding-right: 20px;
`
/** Jaf手配希望押下時のテキストを配置するスタイル設定div */
const JafArrangementDescription = styled.div`
  margin-top: 20px;
`

export default ContractInfo
